import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import {
  ClockCounterClockwise, Funnel, MagnifyingGlass, ChatCircleDots,
  CalendarCheck, UsersThree, FlagCheckered, ShieldCheck, Lightning,
  Sparkle, IdentificationCard, Pulse, Megaphone, Wallet,
} from "@phosphor-icons/react";

/**
 * Mapeamento técnico → linguagem humana.
 *
 * Cada entry retorna:
 *   { icon, group, phrase(detail) }
 *
 * `group` agrupa filtros para o usuário:
 *   atendimento | equipe | agenda | mensagens | sistema | financeiro
 *
 * Códigos não mapeados caem na fallback "executou ação X" — visível,
 * mas só pra admin entender. Garante que NADA é escondido.
 */
const ACTION_DICT = {
  // ── Atendimento (pipeline operacional) ──
  "appointment.create": {
    icon: CalendarCheck, group: "agenda",
    verb: (d) => `agendou um atendimento${d.customer_name ? ` para ${d.customer_name}` : ""}`,
  },
  "appointment.update": {
    icon: CalendarCheck, group: "agenda",
    verb: () => "atualizou um agendamento",
  },
  "appointment.confirm": {
    icon: CalendarCheck, group: "agenda",
    verb: () => "confirmou um agendamento",
  },
  "appointment.cancel": {
    icon: CalendarCheck, group: "agenda",
    verb: () => "cancelou um agendamento",
  },
  "appointment.checked_in": {
    icon: FlagCheckered, group: "atendimento",
    verb: () => "marcou cliente como chegou",
  },
  "appointment.start_service": {
    icon: FlagCheckered, group: "atendimento",
    verb: () => "iniciou um atendimento",
  },
  "appointment.finish": {
    icon: FlagCheckered, group: "atendimento",
    verb: () => "finalizou um atendimento",
  },
  "appointment.attended": {
    icon: FlagCheckered, group: "atendimento",
    verb: () => "registrou comparecimento",
  },
  "appointment.no_show": {
    icon: FlagCheckered, group: "atendimento",
    verb: () => "marcou cliente como não compareceu",
  },
  "appointment.no_show_recovery.sent": {
    icon: ChatCircleDots, group: "atendimento",
    verb: () => "enviou mensagem cordial de não comparecimento",
  },
  "appointment.paid": {
    icon: Wallet, group: "financeiro",
    verb: () => "marcou pagamento como recebido",
  },

  // ── Mensagens ──
  "message.agent_send": {
    icon: ChatCircleDots, group: "mensagens",
    verb: () => "respondeu uma conversa manualmente",
  },
  "message.agent_send_image": {
    icon: ChatCircleDots, group: "mensagens",
    verb: () => "enviou uma imagem em uma conversa",
  },
  "message.agent_send_audio": {
    icon: ChatCircleDots, group: "mensagens",
    verb: () => "enviou um áudio em uma conversa",
  },
  "message.inbound": {
    icon: ChatCircleDots, group: "mensagens",
    verb: (d) => d.handoff_requested ? "recebeu pedido de atendimento humano" : "recebeu uma mensagem",
  },

  // ── Pós-atendimento ──
  "post_service.sent": {
    icon: ChatCircleDots, group: "atendimento",
    verb: () => "enviou mensagem automática de pós-atendimento",
  },
  "post_service.review_request": {
    icon: ChatCircleDots, group: "atendimento",
    verb: () => "pediu avaliação ao cliente",
  },
  "post_service.settings.update": {
    icon: FlagCheckered, group: "sistema",
    verb: () => "atualizou as configurações de pós-atendimento",
  },

  // ── Equipe ──
  "team.invite.created": {
    icon: UsersThree, group: "equipe",
    verb: (d) => `convidou ${d.name || d.email || "novo membro"} como ${roleLbl(d.role)}`,
  },
  "team.invite.resent": {
    icon: UsersThree, group: "equipe",
    verb: (d) => `reenviou o convite para ${d.email || "novo membro"}`,
  },
  "team.invite.revoked": {
    icon: UsersThree, group: "equipe",
    verb: (d) => `cancelou o convite de ${d.email || "membro"}`,
  },
  "team.invite.accepted": {
    icon: UsersThree, group: "equipe",
    verb: (d) => `${d.new_user_name || d.new_user_email || "novo membro"} aceitou o convite`,
  },
  "team.member.update": {
    icon: UsersThree, group: "equipe",
    verb: (d) => {
      const ch = d.changes || {};
      if ("active" in ch) return `${ch.active ? "reativou" : "desativou"} ${d.member_name || d.member_email || "um membro"}`;
      if ("role" in ch) return `mudou ${d.member_name || d.member_email || "um membro"} para ${roleLbl(ch.role)}`;
      return `atualizou ${d.member_name || d.member_email || "um membro"}`;
    },
  },
  "team.member.removed": {
    icon: UsersThree, group: "equipe",
    verb: (d) => `removeu ${d.member_name || d.member_email || "um membro"} da equipe`,
  },

  // ── Clientes ──
  "customer.create": {
    icon: IdentificationCard, group: "atendimento",
    verb: (d) => `cadastrou cliente${d.name ? ` ${d.name}` : ""}`,
  },
  "customer.update": {
    icon: IdentificationCard, group: "atendimento",
    verb: () => "atualizou um cliente",
  },
  "lead.create": {
    icon: IdentificationCard, group: "atendimento",
    verb: (d) => `recebeu novo lead${d.phone ? ` (${d.phone})` : ""}`,
  },

  // ── Conhecimento ──
  "knowledge.create": {
    icon: ShieldCheck, group: "sistema",
    verb: () => "adicionou um documento ao cérebro do AgentUS",
  },
  "knowledge.upload": {
    icon: ShieldCheck, group: "sistema",
    verb: (d) => `importou PDF "${d.filename || "documento"}" no cérebro do AgentUS`,
  },
  "knowledge.update": {
    icon: ShieldCheck, group: "sistema",
    verb: () => "atualizou um documento do cérebro do AgentUS",
  },
  "knowledge.delete": {
    icon: ShieldCheck, group: "sistema",
    verb: () => "removeu um documento do cérebro do AgentUS",
  },

  // ── Campanhas / Ofertas ──
  "campaign.create": {
    icon: Megaphone, group: "mensagens",
    verb: (d) => `criou campanha "${d.name || "sem nome"}"`,
  },
  "campaign.executed": {
    icon: Megaphone, group: "mensagens",
    verb: (d) => `executou campanha (${d.sent || 0} envios)`,
  },

  // ── IA / Decisões ──
  "decision.acted": {
    icon: Pulse, group: "sistema",
    verb: (d) => `IA executou a regra "${d.rule_name || d.rule_code || "automação"}"`,
  },

  // ── Conta ──
  "user.profile_update": {
    icon: IdentificationCard, group: "sistema",
    verb: () => "atualizou o perfil",
  },
  "user.password_change": {
    icon: ShieldCheck, group: "sistema",
    verb: () => "alterou a senha",
  },
  "tenant.profile_update": {
    icon: ShieldCheck, group: "sistema",
    verb: () => "atualizou os dados da empresa",
  },
};

function roleLbl(r) {
  return { admin: "Administrador", operator: "Operador", viewer: "Consulta" }[r] || r || "—";
}

const GROUP_LABEL = {
  all: "Tudo",
  atendimento: "Atendimento",
  agenda: "Agendamentos",
  mensagens: "Mensagens",
  equipe: "Equipe",
  financeiro: "Financeiro",
  sistema: "Sistema",
};

function ago(iso) {
  if (!iso) return "—";
  const now = new Date();
  const t = new Date(iso);
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 60) return "agora";
  const min = Math.round(sec / 60);
  if (min < 60) return `há ${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `há ${hr} h`;
  const d = Math.round(hr / 24);
  if (d < 30) return `há ${d} d`;
  return t.toLocaleDateString("pt-BR");
}

function actorName(log, usersById) {
  const a = log.actor || "";
  if (a.startsWith("user:")) {
    const u = usersById[a.slice(5)];
    return u ? u.name : "Operador";
  }
  if (a.startsWith("super_admin:")) return "AgentUS Master";
  if (a === "agent" || a === "scheduler" || a === "system") return "AgentUS";
  return a || "Sistema";
}

function actorInitials(log, usersById) {
  const n = actorName(log, usersById);
  return n.split(" ").slice(0, 2).map((s) => s[0] || "").join("").toUpperCase();
}

function isIA(log) {
  const a = log.actor || "";
  return a === "agent" || a === "scheduler" || a === "system";
}

export default function AuditTimeline() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  // v0.97-c — filtro "por pessoa" pro admin investigar cirurgicamente.
  // Valores: "all" | "ai" | "user:<id>"
  const [actorFilter, setActorFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/audit?limit=400"),
      api.get("/team"),
    ]).then(([logsRes, teamRes]) => {
      setLogs(logsRes.data || []);
      const members = teamRes.data?.members || [];
      const byId = {};
      members.forEach((m) => { byId[m.id] = m; });
      setUsers(byId);
      setTeamMembers(members);
    }).finally(() => setLoading(false));
  }, []);

  // Apply filters in-memory — audit list is small (max 400)
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      const entry = ACTION_DICT[l.action];
      if (groupFilter !== "all") {
        const grp = entry?.group || "sistema";
        if (grp !== groupFilter) return false;
      }
      // Filtro por pessoa (dropdown "Todos" | "AgentUS (IA)" | membro X)
      if (actorFilter !== "all") {
        if (actorFilter === "ai") {
          if (!isIA(l)) return false;
        } else if (actorFilter.startsWith("user:")) {
          if ((l.actor || "") !== actorFilter) return false;
        }
      }
      if (q) {
        const human = entry ? safeRender(entry.verb, l.detail) : l.action;
        const actor = actorName(l, users);
        const hay = `${human} ${actor} ${l.action}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [logs, search, groupFilter, actorFilter, users]);

  const counts = useMemo(() => {
    const c = { all: logs.length };
    for (const l of logs) {
      const grp = ACTION_DICT[l.action]?.group || "sistema";
      c[grp] = (c[grp] || 0) + 1;
    }
    return c;
  }, [logs]);

  return (
    <>
      <Topbar
        title="Auditoria"
        subtitle="Tudo o que aconteceu na sua conta — IA e equipe — em linguagem clara."
      />

      <div className="p-6 md:p-8 space-y-5">
        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2">
          {Object.keys(GROUP_LABEL).map((k) => (
            <button
              key={k}
              onClick={() => setGroupFilter(k)}
              data-testid={`audit-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors inline-flex items-center gap-1.5 ${groupFilter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
            >
              {GROUP_LABEL[k]}
              <span className={`text-[10px] tabular-nums ${groupFilter === k ? "text-white/80" : "text-slate-400"}`}>
                {counts[k] || 0}
              </span>
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            {/* Filtro por pessoa — só útil quando há 2+ membros */}
            {teamMembers.length > 1 && (
              <select
                value={actorFilter}
                onChange={(e) => setActorFilter(e.target.value)}
                data-testid="audit-actor-filter"
                className="rounded-full border border-slate-200 text-[12px] px-3 py-1.5 bg-white focus:outline-none focus:border-slate-900 cursor-pointer"
              >
                <option value="all">Todas as pessoas</option>
                <option value="ai">AgentUS (IA)</option>
                <optgroup label="Equipe">
                  {teamMembers.map((m) => (
                    <option key={m.id} value={`user:${m.id}`}>{m.name}</option>
                  ))}
                </optgroup>
              </select>
            )}
            <div className="relative">
              <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por nome, ação…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="audit-search"
                className="pl-7 pr-3 py-1.5 rounded-full border border-slate-200 text-[12px] w-52 focus:outline-none focus:border-slate-900 bg-white"
              />
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? (
            <div className="p-8 text-sm text-slate-500">Carregando…</div>
          ) : visible.length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-500">
              <ClockCounterClockwise size={28} weight="duotone" className="mx-auto text-slate-300 mb-2" />
              Nenhum evento corresponde ao filtro atual.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {visible.map((l) => {
                const entry = ACTION_DICT[l.action];
                const Icon = entry?.icon || Lightning;
                const phrase = entry ? safeRender(entry.verb, l.detail) : `executou ação \`${l.action}\``;
                const name = actorName(l, users);
                const aiBadge = isIA(l);
                return (
                  <li key={l.id} data-testid={`audit-item-${l.id}`} className="px-4 md:px-5 py-3 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${aiBadge ? 'bg-slate-100 text-slate-700' : 'bg-slate-900 text-white'}" style={{
                      backgroundColor: aiBadge ? "#F1F5F9" : "#0F172A",
                      color: aiBadge ? "#334155" : "white",
                    }}>
                      {aiBadge ? <Sparkle size={15} weight="fill" /> : <span className="text-[10px] font-semibold tracking-wider">{actorInitials(l, users)}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 leading-snug">
                        <span className="font-medium">{name}</span>{" "}
                        <span className="text-slate-700">{phrase}</span>
                      </p>
                      <div className="text-[11.5px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{ago(l.created_at)}</span>
                        {entry?.group && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-50 text-slate-500">
                            <Icon size={10} weight="fill" /> {GROUP_LABEL[entry.group] || entry.group}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

function safeRender(verb, detail) {
  try {
    return verb(detail || {});
  } catch {
    return "executou uma ação";
  }
}
